import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  deleteIPWhitelistAction,
  getIPWhitelistAction,
  upsertIPWhitelistAction,
} from 'actions/IPWhitelistActions';
import { InfoHeader } from 'components/custom/info_header';
import { CustomTable } from 'components/custom/table/CustomTable';
import { TopHeader } from 'components/layouts/page/topHeader';
import SideNavbar from 'components/layouts/side_navbar';
import { isOwner } from 'constants/Roles';
import { useTranslate } from 'context/TranslateContext';
import { useSwal } from 'helpers/sweetalert';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FunctionDispatch, GlobalTypes } from 'redux/types';
import { PaginationProps } from 'services/API/common/Fetcher';
import { IPWhitelistItem, IPWhitelistMutationRequest } from 'services/API/IPWhitelist/interface';
import { IPWhitelistModal, IPWhitelistModalForm } from './Modal';

const PAGE_SIZE = 10;

export const IPWhitelist = () => {
  const {
    auth: {
      user: { company_id, role },
    },
  } = useSelector((state: GlobalTypes.RootState) => state);

  const dispatch = useDispatch<FunctionDispatch>();
  const { translate } = useTranslate();
  const [Swal] = useSwal();

  const [items, setItems] = useState<Array<IPWhitelistItem>>([]);
  const [pagination, setPagination] = useState<PaginationProps | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState<IPWhitelistItem | null>(null);

  const fetchData = async () => {
    if (!company_id) {
      return;
    }
    setIsLoading(true);
    const res = await dispatch(
      getIPWhitelistAction(company_id, { page: currentPage, limit: PAGE_SIZE }),
    );
    setIsLoading(false);
    if (!res.success) {
      Swal.fire({
        icon: 'error',
        title: translate('ip_whitelist_title'),
        text: res?.message ?? 'Oops',
      });
      return;
    }
    setItems(res.data ?? []);
    setPagination(res.meta?.pagination ?? null);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, company_id]);

  const handleUpsert = async (payload: IPWhitelistMutationRequest) => {
    if (!company_id) {
      return false;
    }
    const response = await dispatch(upsertIPWhitelistAction(company_id, payload));
    if (!response.success) {
      Swal.fire({
        icon: 'error',
        title: translate('ip_whitelist_title'),
        text: response?.message ?? 'Oops',
      });
      return false;
    }
    fetchData();
    return true;
  };

  const handleCreateSubmit = (form: IPWhitelistModalForm) => handleUpsert(form);

  const handleEditSubmit = (form: IPWhitelistModalForm) => {
    if (!selectedRule) {
      return Promise.resolve(false);
    }
    return handleUpsert({ ...form, id: selectedRule.id });
  };

  const deleteRule = async (rule: IPWhitelistItem) => {
    if (!company_id) {
      return;
    }
    const res = await dispatch(deleteIPWhitelistAction(company_id, rule.id));
    const text = res.success ? translate('ip_whitelist_delete_success') : res?.message ?? 'Oops';
    await Swal.fire({
      icon: res.success ? 'success' : 'error',
      title: translate('ip_whitelist_title'),
      text,
    });
    if (!res.success) {
      return;
    }
    await fetchData();
  };

  const confirmDelete = (rule: IPWhitelistItem) => {
    Swal.fire({
      icon: 'warning',
      title: translate('ip_whitelist_title'),
      text: translate('ip_whitelist_are_you_sure_delete_rule'),
      showCancelButton: true,
      showConfirmButton: true,
      preConfirm: () => deleteRule(rule),
    });
  };

  const onEditClick = (rule: IPWhitelistItem) => {
    setSelectedRule(rule);
    setIsEditOpen(true);
  };

  const onCreateClick = () => {
    setSelectedRule(null);
    setIsCreateOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateOpen(false);
  };

  const closeEditModal = () => {
    setIsEditOpen(false);
    setSelectedRule(null);
  };

  const renderStatus = (rule: IPWhitelistItem) => (
    <span className={`badge ${rule.is_enabled ? 'bg-success' : 'bg-secondary'}`}>
      {translate(rule.is_enabled ? 'active' : 'inactive')}
    </span>
  );

  return (
    <>
      <TopHeader title={translate('company_info')} />
      <SideNavbar location={location as any} type="admin" />
      <div id="main-content" className="hide-scrollbar">
        <div className="box">
          <InfoHeader src="company-info" name="ip_whitelist_title" />
          <div className="pt-3 bg-white">
            {isOwner(role) && (
              <div className="document mt-2 mb-3 mx-3 dash-border">
                <button
                  className="btn btn-outline-secondary"
                  style={{ margin: 'auto' }}
                  onClick={onCreateClick}>
                  {translate('add')}
                </button>
              </div>
            )}
            <CustomTable
              totalColumn={5}
              pagination={pagination}
              setCurrentPage={setCurrentPage}
              isLoading={isLoading}
              showOldDataWhenReload={false}
              renderHeading={() => (
                <tr>
                  <th scope="col" className="border-top-0">
                    {translate('ip_whitelist_cidr')}
                  </th>
                  <th scope="col" className="border-top-0">
                    {translate('description')}
                  </th>
                  <th scope="col" className="border-top-0">
                    {translate('status')}
                  </th>
                  <th scope="col" className="border-top-0">
                    {translate('updated_at')}
                  </th>
                  <th scope="col" className="border-top-0">
                    {translate('actions')}
                  </th>
                </tr>
              )}
              renderData={() =>
                items.map((rule) => (
                  <tr key={rule.id}>
                    <th scope="row" className="align-middle">
                      {rule.ip_address}
                    </th>
                    <td className="align-middle">{rule.description || '-'}</td>
                    <td className="align-middle">{renderStatus(rule)}</td>
                    <td className="align-middle">{rule.updated_at || rule.created_at || '-'}</td>
                    <td className="align-middle">
                      <div className="d-flex align-items-center">
                        <button
                          className="btn btn-outline-primary btn-sm me-2"
                          onClick={() => onEditClick(rule)}>
                          <FontAwesomeIcon icon={faPen} />
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => confirmDelete(rule)}>
                          <FontAwesomeIcon icon={faTrash} color="#ffffff" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              }
              centerPaginate={true}
            />
          </div>
        </div>
      </div>
      {isCreateOpen && (
        <IPWhitelistModal
          isOpen={isCreateOpen}
          mode="create"
          onClose={closeCreateModal}
          onSuccess={closeCreateModal}
          onSubmit={handleCreateSubmit}
        />
      )}
      {isEditOpen && (
        <IPWhitelistModal
          isOpen={isEditOpen}
          mode="edit"
          initialValue={
            selectedRule && {
              ip_address: selectedRule.ip_address,
              description: selectedRule.description ?? '',
              is_enabled: selectedRule.is_enabled,
            }
          }
          onClose={closeEditModal}
          onSuccess={closeEditModal}
          onSubmit={handleEditSubmit}
        />
      )}
    </>
  );
};

export default IPWhitelist;
